// Avisos da UC para o faturamento (tabela `lembretes_faturamento`).
//
// Até 04/11/2026 cada mês marcado virava uma cópia do aviso: 1.219 cópias para 124
// avisos, e 102 de 150 lotes criados com os 12 meses que a tela oferecia — o time
// queria regra permanente e não tinha como dizer isso. Desde a migration
// 20261104120000 o aviso é um registro só, com início e fim (`mes_inicio`,
// `mes_fim`; fim vazio = sem prazo). As colunas antigas (`mes_referencia`,
// `data_inicio`, `data_fim`) continuam sendo lidas para o que ainda vier nelas.
//
// "Avisos" aqui não são os avisos de vencimento por WhatsApp (`lembrete_cobranca_log`,
// tela "Lembretes de Cobrança"): estes são instruções para quem calcula a fatura.
//
// Sem imports de runtime: roda sob `node --test`.

export const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'] as const;

export type TipoAviso = 'desconto' | 'acrescimo' | 'outro';

export interface AvisoUc {
    id: string;
    uc_id?: number | string | null;
    uc_codigo?: string | null;
    tipo: TipoAviso | string;
    descricao: string;
    status: string | null;
    mes_inicio?: string | null;
    mes_fim?: string | null;
    mes_referencia?: string | null;
    data_inicio?: string | null;
    data_fim?: string | null;
    criado_por_nome?: string | null;
    created_at?: string | null;
    motivo_descarte?: string | null;
    data_conclusao?: string | null;
    encerrado_por_nome?: string | null;
}

/** "OUT/2026" → número do mês (ano × 12 + mês). Nulo se não for um mês válido. */
export function indiceDoMesRef(mesRef: string | null | undefined): number | null {
    const [mes, ano] = String(mesRef ?? '').trim().toUpperCase().split('/');
    const i = MESES.indexOf(mes as typeof MESES[number]);
    const a = Number(ano);
    if (i < 0 || !Number.isInteger(a) || a < 2000) return null;
    return a * 12 + i;
}

/** "2026-10-01" (ou timestamp) → número do mês. */
export function indiceDaData(data: string | null | undefined): number | null {
    const m = /^(\d{4})-(\d{2})/.exec(String(data ?? ''));
    if (!m) return null;
    const mes = Number(m[2]);
    if (mes < 1 || mes > 12) return null;
    return Number(m[1]) * 12 + (mes - 1);
}

export const mesRefDoIndice = (i: number): string => `${MESES[((i % 12) + 12) % 12]}/${Math.floor(i / 12)}`;

const dois = (n: number) => String(n).padStart(2, '0');

/** Primeiro dia do mês, "2026-10-01". */
export const dataDoIndice = (i: number): string => `${Math.floor(i / 12)}-${dois((((i % 12) + 12) % 12) + 1)}-01`;

/** Último dia do mês, "2026-10-31". */
export function ultimoDiaDoIndice(i: number): string {
    const ano = Math.floor(i / 12);
    const mes = ((i % 12) + 12) % 12;
    const dia = new Date(Date.UTC(ano, mes + 1, 0)).getUTCDate();
    return `${ano}-${dois(mes + 1)}-${dois(dia)}`;
}

/**
 * Mês de referência que o Financeiro está faturando: o mês anterior ao de hoje.
 * Em novembro calcula-se a fatura de OUT.
 */
export function indiceMesAtual(hoje: Date = new Date()): number {
    return hoje.getFullYear() * 12 + hoje.getMonth() - 1;
}

/** Meses que a tela oferece: o que está sendo faturado e os 12 seguintes. */
export function mesesParaEscolher(hoje: Date = new Date(), quantidade = 13): string[] {
    const inicio = indiceMesAtual(hoje);
    return Array.from({ length: quantidade }, (_, k) => mesRefDoIndice(inicio + k));
}

export interface Periodo {
    /** Nulo = aviso sem mês legível (não vale em fatura nenhuma). */
    ini: number | null;
    /** Nulo = sem prazo. */
    fim: number | null;
}

export function periodoDoAviso(a: AvisoUc): Periodo {
    if (a.mes_inicio) {
        return { ini: indiceDaData(a.mes_inicio), fim: a.mes_fim ? indiceDaData(a.mes_fim) : null };
    }
    const unico = indiceDoMesRef(a.mes_referencia);
    if (unico !== null) return { ini: unico, fim: unico };
    if (a.data_inicio) {
        return { ini: indiceDaData(a.data_inicio), fim: a.data_fim ? indiceDaData(a.data_fim) : null };
    }
    return { ini: null, fim: null };
}

/** O aviso vale na fatura deste mês de referência ("OUT/2026")? */
export function avisoValeNoMes(a: AvisoUc, mesRef: string | null | undefined): boolean {
    const alvo = indiceDoMesRef(mesRef);
    const { ini, fim } = periodoDoAviso(a);
    if (alvo === null || ini === null) return false;
    return ini <= alvo && (fim === null || alvo <= fim);
}

/** Este é o último mês do aviso? (para quem calcula renovar, se precisar) */
export function ehUltimoMes(a: AvisoUc, mesRef: string | null | undefined): boolean {
    const alvo = indiceDoMesRef(mesRef);
    const { fim } = periodoDoAviso(a);
    return alvo !== null && fim !== null && fim === alvo;
}

export type SituacaoAviso = 'ativo' | 'futuro' | 'terminado' | 'encerrado';

/**
 * - ativo: vale no mês que está sendo faturado (ou começou e não tem fim)
 * - futuro: começa depois
 * - terminado: o prazo passou, ninguém encerrou — some do cálculo sozinho
 * - encerrado: alguém encerrou (ou é dos status antigos concluído/descartado)
 */
export function situacaoDoAviso(a: AvisoUc, mesAtual: number): SituacaoAviso {
    if ((a.status ?? 'pendente') !== 'pendente') return 'encerrado';
    const { ini, fim } = periodoDoAviso(a);
    if (ini === null) return 'terminado';
    if (ini > mesAtual) return 'futuro';
    if (fim !== null && fim < mesAtual) return 'terminado';
    return 'ativo';
}

export function textoPeriodo(a: AvisoUc): string {
    const { ini, fim } = periodoDoAviso(a);
    if (ini === null) return 'Sem mês definido';
    if (fim === null) return `A partir de ${mesRefDoIndice(ini)} · sem prazo`;
    if (fim === ini) return `Só ${mesRefDoIndice(ini)}`;
    return `${mesRefDoIndice(ini)} → ${mesRefDoIndice(fim)}`;
}

export type AteQuando =
    | { tipo: 'sem_prazo' }
    | { tipo: 'so_este' }
    | { tipo: 'ate'; mes: string };

export interface PeriodoGravavel {
    mes_inicio: string;
    mes_fim: string | null;
    // Colunas antigas, preenchidas para quem ainda estiver com a tela anterior aberta
    // durante a publicação: ela mostra o aviso pelo mês único ou pelo intervalo de datas.
    mes_referencia: string | null;
    data_inicio: string;
    data_fim: string | null;
}

/** Período escolhido na tela → colunas do banco. Devolve texto de erro quando não fecha. */
export function periodoParaGravar(inicio: string, ate: AteQuando): PeriodoGravavel | string {
    const ini = indiceDoMesRef(inicio);
    if (ini === null) return 'Escolha o mês em que o aviso começa.';
    let fim: number | null;
    if (ate.tipo === 'sem_prazo') fim = null;
    else if (ate.tipo === 'so_este') fim = ini;
    else {
        fim = indiceDoMesRef(ate.mes);
        if (fim === null) return 'Escolha até que mês o aviso vale.';
        if (fim < ini) return 'O mês final não pode ser antes do mês de início.';
    }
    return {
        mes_inicio: dataDoIndice(ini),
        mes_fim: fim === null ? null : dataDoIndice(fim),
        mes_referencia: fim === ini ? mesRefDoIndice(ini) : null,
        data_inicio: dataDoIndice(ini),
        data_fim: fim === null ? null : ultimoDiaDoIndice(fim),
    };
}

const ORDEM: Record<SituacaoAviso, number> = { ativo: 0, futuro: 1, terminado: 2, encerrado: 3 };

/** Ativos primeiro; dentro de cada grupo, os mais recentes primeiro. */
export function ordenarAvisos<T extends AvisoUc>(lista: T[], mesAtual: number): T[] {
    return [...lista].sort((a, b) => {
        const s = ORDEM[situacaoDoAviso(a, mesAtual)] - ORDEM[situacaoDoAviso(b, mesAtual)];
        if (s !== 0) return s;
        return String(b.created_at ?? '').localeCompare(String(a.created_at ?? ''));
    });
}
